import { Login, Public, PublicOff } from "@mui/icons-material";
import { type ReactNode } from "react";

import { useDoLogin } from "~/components/topbar/LoginButton";
import { useRouteParams } from "~/data/route";
import { useIsFeatureFlagEnabled, useSharingSyncState } from "~/state";
import { Flag } from "~/state/types";
import { Button, Button as UiButton } from "~/ui/Button";
import { Popover, PopoverContent, PopoverTrigger } from "~/ui/Popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/ui/Tabs";
import { cn } from "~/ui/utils";

import { useAuth } from "../../../../auth/useAuth";
import { usePropertiesPane } from "../../easel/properties-pane/usePropertiesPane";
import { useSyncDocument } from "../../properties-pane/publish-tab/usePublish";
import { PublishTab } from "./PublishTab";
import { ShareTab } from "./ShareTab";
import { TransferTab } from "./TransferTab";

// UX Iterations:
// Step 1: generate share links (all users are readers) if published <-- WE ARE HERE
// Step 2: allow individual users to be added as readers
// Step 3: allow individual users to be added with additional permissions

export const PublishDocumentButton = () => {
	const [doLogin, loginDisabled] = useDoLogin();
	const {
		isSyncing,

		syncStatus,
		planStatus,
	} = useSyncDocument({ mode: "publish-if-unpublished" });
	const propertiesPane = usePropertiesPane();
	const { user } = useAuth();

	const { documentId } = useRouteParams();
	const { isLoading: isSharingStateLoading } = useSharingSyncState(documentId);

	const shareTabEnabled = useIsFeatureFlagEnabled(Flag.ShareTab);

	let contentWidth = "!w-96";
	if (planStatus === "unachievable-not-on-synced-branch") {
		contentWidth = "!w-fit";
	} else if (planStatus === "unachievable-behind-remote") {
		contentWidth = "!w-fit";
	} else if (planStatus === "unachievable-unauthorized") {
		contentWidth = "!w-fit";
	} else if (planStatus === "unachievable-not-authenticated") {
		contentWidth = "!w-fit";
	}

	let content: ReactNode;
	if (planStatus === "unachievable-not-authenticated") {
		content = (
			<>
				<div className="flex flex-col gap-2 text-center px-4 pt-4 rounded-t-md">
					<div className="text-base font-medium">Share Document</div>
					<div className="text-sm">You must be logged in to share a document.</div>
				</div>
				<div className="flex px-4 pb-4 gap-2">
					<Button
						className="grow"
						preset="primary"
						size="sm"
						elevation="flat"
						startIcon={<Login />}
						onClick={doLogin}
						disabled={loginDisabled}
					>
						Log in
					</Button>
				</div>
			</>
		);
	} else if (shareTabEnabled && user?.isMomentEmployee) {
		content = (
			<Tabs defaultValue="sharing">
				<TabsList>
					<TabsTrigger value="sharing">Share</TabsTrigger>
					<TabsTrigger value="publish">Publish</TabsTrigger>
					<TabsTrigger value="transfer">Transfer</TabsTrigger>
				</TabsList>
				<TabsContent value="sharing">
					<ShareTab />
				</TabsContent>
				<TabsContent value="publish">
					<PublishTab />
				</TabsContent>
				<TabsContent value="transfer">
					<TransferTab />
				</TabsContent>
			</Tabs>
		);
	} else if (shareTabEnabled) {
		content = (
			<Tabs defaultValue="sharing">
				<TabsList>
					<TabsTrigger value="sharing">Share</TabsTrigger>
					<TabsTrigger value="publish">Publish</TabsTrigger>
				</TabsList>
				<TabsContent value="sharing">
					<ShareTab />
				</TabsContent>
				<TabsContent value="publish">
					<PublishTab />
				</TabsContent>
			</Tabs>
		);
	} else {
		content = <PublishTab />;
	}

	return (
		<Popover>
			<PopoverTrigger asChild>
				<UiButton
					preset="quaternary"
					size="sm"
					startIcon={syncStatus === "published" ? <Public /> : <PublicOff />}
					borderRadius="default"
					toggled={propertiesPane.paneState.pane === "publish"}
					loading={isSyncing || isSharingStateLoading}
				>
					Share
				</UiButton>
			</PopoverTrigger>
			<PopoverContent className={cn("relative !p-0 flex flex-col gap-4 mr-2 ", contentWidth)}>
				{content}
			</PopoverContent>
		</Popover>
	);
};
